import axios from 'axios'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import { grey400 } from 'ext-ui/style/colors'
import ReceiptMismatchIcon from 'ext-ui/svg/icon/ReceiptMismatchIcon'
import _ from 'lodash'
import React, { useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { SingleReceiptReminderSuccessToastMessage } from 'src/app/Toast/Toast'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { useRequestConfig } from 'src/queries/queries.utils'
import { css } from 'styled-components'

interface SingleReceiptReminderProps {
  transactionId: string
  name?: string
  wrapText?: boolean
  maxWidth?: number
}

const SingleReceiptReminder: React.FC<SingleReceiptReminderProps> = (
  props,
): React.ReactElement => {
  const authedJsonApiRequestConfig = useRequestConfig()
  const { analytics } = useAnalyticsContext()

  const [isDisabled, setIsDisabled] = useState<boolean>(false)

  const dispatch = useBackgroundDispatch()
  const translate = useTranslate()

  const handleTrackReceiptReminder = (reminderSent: boolean): void => {
    analytics?.track('Transactions - Receipt Reminder - button press', {
      reminder_type: 'single notification',
      reminder_sent: reminderSent,
    })
  }

  const handleOnClick = (e: React.MouseEvent): void => {
    e.stopPropagation()

    // Isolated axios call since no redux is needed and the endpoint is only used here
    if (!isDisabled) {
      axios
        .post(
          `/transactions/${props.transactionId}/receiptreminder`,
          {},
          authedJsonApiRequestConfig,
        )
        .then(
          () => {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <SingleReceiptReminderSuccessToastMessage name={props.name} />
                ),
              }),
            )
            handleTrackReceiptReminder(true)
            setIsDisabled(true)
          },
          (err) => {
            const error = _.get(err, 'response.data.error', undefined)
            error !== undefined &&
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: <span>{error}</span>,
                  failureColorScheme: true,
                  hideImage: true,
                }),
              )
            handleTrackReceiptReminder(false)
            setIsDisabled(true)
          },
        )
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      onClick={handleOnClick}
      role={'button'}
      tabIndex={0}
      data-testid={
        isDisabled ? 'sentReceiptReminderIcon' : 'missingReceiptsBtn'
      }
    >
      {
        <TooltipIcon
          icon={
            <span
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                padding: 8px;
                border-radius: 8px;
                &:hover {
                  background: ${grey400};
                }
              `}
            >
              <ReceiptMismatchIcon width={16} height={16} />
            </span>
          }
        >
          {translate(
            'Remind this person to send a receipt for this transaction',
          )}
        </TooltipIcon>
      }
    </div>
  )
}

export default SingleReceiptReminder
